import { router } from '@inertiajs/react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { MoneyDisplay } from '@/components/MoneyDisplay'
import { InlineEditField } from './InlineEditField'
import type { Transaction, Category, Account, Receipt } from '@/types'
import { Checkbox } from '@/components/ui/checkbox'
import { FileTextIcon, FilePlusIcon, Trash2Icon, DownloadIcon, ReceiptIcon } from 'lucide-react'
import { useRef, useState } from 'react'

interface TransactionsTableProps {
  transactions: Transaction[]
  categories: Category[]
  account: Account | null
}

export function TransactionsTable({ transactions, categories, account }: TransactionsTableProps) {
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null)

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="rounded-full bg-muted p-3.5">
          <ReceiptIcon className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mt-3 text-[15px] font-medium text-foreground">No transactions</h3>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Get started by importing transactions or adding one manually.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[130px] pl-4 text-[13px] font-medium text-muted-foreground">Date</TableHead>
              <TableHead className="text-[13px] font-medium text-muted-foreground">Description</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead className="w-[130px] text-right text-[13px] font-medium text-muted-foreground">Amount</TableHead>
              <TableHead className="hidden md:table-cell w-[60px] text-center text-[13px] font-medium text-muted-foreground">Tax</TableHead>
              <TableHead className="hidden md:table-cell w-[90px] text-[13px] font-medium text-muted-foreground">Receipts</TableHead>
              <TableHead className="w-[170px] text-[13px] font-medium text-muted-foreground">Category</TableHead>
              <TableHead className="hidden md:table-cell w-[40px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.map((transaction) => (
              <TransactionRow
                key={transaction.id}
                transaction={transaction}
                categories={categories}
                account={account}
                onReceiptClick={setSelectedReceipt}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selectedReceipt} onOpenChange={() => setSelectedReceipt(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-auto">
          {selectedReceipt && (
            <>
              {selectedReceipt.content_type === 'application/pdf' ? (
                <iframe
                  src={selectedReceipt.url}
                  title={selectedReceipt.filename}
                  className="w-full h-[75vh] rounded-lg"
                />
              ) : (
                <img
                  src={selectedReceipt.url}
                  alt={selectedReceipt.filename}
                  className="w-full object-contain rounded-lg"
                />
              )}
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" size="sm" asChild>
                  <a href={`${selectedReceipt.url}?disposition=attachment`} download={selectedReceipt.filename}>
                    <DownloadIcon className="h-3.5 w-3.5 mr-1.5" />
                    Download
                  </a>
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this receipt?')) {
                      router.delete(`/attachments/${selectedReceipt.id}`, {
                        preserveScroll: true,
                        onSuccess: () => setSelectedReceipt(null),
                      })
                    }
                  }}
                >
                  <Trash2Icon className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

function TransactionRow({
  transaction,
  categories,
  account,
  onReceiptClick,
}: {
  transaction: Transaction
  categories: Category[]
  account: Account | null
  onReceiptClick: (receipt: Receipt) => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const formattedDate = new Date(transaction.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  function handleCategoryChange(value: string) {
    router.patch(`/transactions/${transaction.id}`, {
      transaction: { id: transaction.id, category_id: value === '__none__' ? null : value },
    }, { preserveScroll: true })
  }

  function handleTaxToggle(checked: boolean | 'indeterminate') {
    if (checked === 'indeterminate') return
    router.patch(`/transactions/${transaction.id}`, {
      transaction: { id: transaction.id, has_tax: checked },
    }, { preserveScroll: true })
  }

  function handleReceiptUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files?.length) return

    const formData = new FormData()
    formData.append('transaction[id]', String(transaction.id))
    transaction.receipts.forEach((r) => {
      formData.append('transaction[receipts][]', r.signed_id)
    })
    Array.from(files).forEach((file) => {
      formData.append('transaction[receipts][]', file)
    })

    router.patch(`/transactions/${transaction.id}`, formData, {
      forceFormData: true,
      preserveScroll: true,
    })
  }

  const filteredCategories = categories.filter((c) => {
    if (c.kind === 'transfer') return true
    if (transaction.amount.cents >= 0) return c.kind === 'revenue'
    return c.kind === 'cost'
  })

  return (
    <TableRow className="group transition-colors hover:bg-accent/30">
      <TableCell className="pl-4 tabular-nums text-[13px] text-muted-foreground">
        {account?.supports_manual_entry ? (
          <InlineEditField
            transactionId={transaction.id}
            field="date"
            value={transaction.date}
            displayValue={formattedDate}
            type="date"
          />
        ) : (
          formattedDate
        )}
      </TableCell>

      <TableCell>
        <div className="space-y-0.5">
          <p className="text-[11px] text-muted-foreground truncate max-w-[280px]">
            {transaction.description}
          </p>
          <InlineEditField
            transactionId={transaction.id}
            field="memo"
            value={transaction.memo || ''}
            className="text-[13px] font-medium text-foreground"
            placeholder="Add memo..."
          />
        </div>
      </TableCell>

      <TableCell>
        <InlineEditField transactionId={transaction.id} field="vendor" value={transaction.vendor || ''} placeholder="Add vendor..." />
        {transaction.vendor_review_required && !transaction.vendor && <span className="text-xs text-amber-700">Review invoice</span>}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <InlineEditField
          transactionId={transaction.id}
          field="amount"
          // The server assigns `amount` in major units (dollars for Wise USD rows).
          value={String(transaction.amount.cents / (transaction.amount.currency === 'JPY' ? 1 : 100))}
          displayValue={
            <MoneyDisplay money={transaction.amount} colorize /> as unknown as string
          }
          type="number"
          className="justify-end"
        />
      </TableCell>

      <TableCell className="hidden md:table-cell text-center">
        <Checkbox
          checked={transaction.has_tax}
          onCheckedChange={handleTaxToggle}
          aria-label="Has tax"
        />
      </TableCell>

      <TableCell className="hidden md:table-cell">
        <div className="flex items-center gap-0.5">
          {transaction.receipts.map((receipt) => (
            <button
              key={receipt.id}
              type="button"
              onClick={() => onReceiptClick(receipt)}
              className="rounded p-1 transition-colors hover:bg-muted"
            >
              <FileTextIcon className="h-3.5 w-3.5 text-primary" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FilePlusIcon className="h-3.5 w-3.5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleReceiptUpload}
          />
        </div>
      </TableCell>

      <TableCell>
        <Select
          value={transaction.category_id?.toString() || '__none__'}
          onValueChange={handleCategoryChange}
        >
          <SelectTrigger className="h-7 w-full text-[13px]">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No category</SelectItem>
            {filteredCategories.map((c) => (
              <SelectItem key={c.id} value={c.id.toString()}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      <TableCell className="hidden md:table-cell">
        {account?.supports_manual_entry && (
          <button
            type="button"
            onClick={() => router.delete(`/transactions/${transaction.id}`, { preserveScroll: true })}
            className="rounded p-1 text-muted-foreground opacity-0 transition-all hover:bg-destructive/8 hover:text-destructive group-hover:opacity-100"
          >
            <Trash2Icon className="h-3.5 w-3.5" />
          </button>
        )}
      </TableCell>
    </TableRow>
  )
}
